import { create } from 'zustand';
import { bundledDocument, fetchDocument, savedDocument, saveDocument } from './documents.js';

/**
 * The device's agreement to the Terms and the Privacy Policy (D10,
 * `docs/legal/policy-decisions.md`).
 *
 * Every install agrees before first use, guests included, and the guest is
 * created only after the tap. So the record lives on the device first: it is
 * written the moment the player agrees, works offline, and goes up with the
 * device's registration. A sign-in to another player on an agreed device sends
 * the same record for that player too. `recordedFor` lists the players the
 * server already holds it for.
 *
 * A version names the last material change to a document. A minor edit keeps
 * it, so it asks nobody again. The server's copy is the authority: the app
 * pulls both documents at launch and on reconnect (`refreshPolicyVersions`),
 * so a new version reaches every device without an app release (Mitch,
 * 2026-10-01). A device whose record is older agrees once more, on the
 * terms-changed screen. Offline, the newest copy the device has read stands,
 * and the bundle is the floor.
 */
export interface Agreement {
  readonly termsVersion: string;
  readonly privacyVersion: string;
  /** When the player tapped, as an ISO timestamp. */
  readonly acceptedAt: string;
  /** The players the server holds this record for. */
  readonly recordedFor: readonly string[];
}

/** What the device has agreed to: nothing, the current documents, or older ones. */
export type AgreementStatus = 'none' | 'current' | 'outdated';

const KEY = 'cribbage.agreement.v1';
const VERSION = /^\d{4}-\d{2}-\d{2}$/;

/** The versions a player is asked to agree to. */
export interface PolicyVersions {
  readonly terms: string;
  readonly privacy: string;
}

/**
 * The versions of the copies this device reads (`savedDocument`), so a player
 * always agrees to the text the legal pages show.
 */
function readVersions(): PolicyVersions {
  return {
    terms: savedDocument(bundledDocument('terms')).version,
    privacy: savedDocument(bundledDocument('privacy')).version,
  };
}

/** A stored record, checked field by field, or null. */
function parseAgreement(value: unknown): Agreement | null {
  if (typeof value !== 'object' || value === null) return null;
  const { termsVersion, privacyVersion, acceptedAt, recordedFor } = value as Record<
    string,
    unknown
  >;
  if (typeof termsVersion !== 'string' || !VERSION.test(termsVersion)) return null;
  if (typeof privacyVersion !== 'string' || !VERSION.test(privacyVersion)) return null;
  if (typeof acceptedAt !== 'string' || Number.isNaN(Date.parse(acceptedAt))) return null;
  if (!Array.isArray(recordedFor) || !recordedFor.every((id) => typeof id === 'string')) {
    return null;
  }
  return { termsVersion, privacyVersion, acceptedAt, recordedFor };
}

function readAgreement(): Agreement | null {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    return raw ? parseAgreement(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function writeAgreement(agreement: Agreement): void {
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(agreement));
  } catch {
    // A browser that keeps nothing still agreed for this visit. It is never
    // given a token either (`canKeepToken`), so there is nothing to send.
  }
}

export function statusOf(agreement: Agreement | null, versions: PolicyVersions): AgreementStatus {
  if (agreement === null) return 'none';
  return agreement.termsVersion === versions.terms && agreement.privacyVersion === versions.privacy
    ? 'current'
    : 'outdated';
}

interface AgreementState {
  readonly agreement: Agreement | null;
  /** What the device asks for now. Moves when a newer document is saved. */
  readonly versions: PolicyVersions;
  /** Read the saved documents again, after one of them was refreshed. */
  syncVersions(): void;
  /** Agree to the current documents, now. A new record, so nobody holds it yet. */
  agree(): void;
  /** The server now holds this record for this player. */
  markRecorded(playerId: string, agreement: Agreement): void;
}

export const useAgreementStore = create<AgreementState>((set, get) => ({
  agreement: readAgreement(),
  versions: readVersions(),

  syncVersions() {
    const next = readVersions();
    const { versions } = get();
    if (next.terms !== versions.terms || next.privacy !== versions.privacy) {
      set({ versions: next });
    }
  },

  agree() {
    const { versions } = get();
    const agreement: Agreement = {
      termsVersion: versions.terms,
      privacyVersion: versions.privacy,
      acceptedAt: new Date().toISOString(),
      recordedFor: [],
    };
    writeAgreement(agreement);
    set({ agreement });
  },

  markRecorded(playerId, agreement) {
    // Only the record that was sent. One agreed while it was in the air has
    // not reached the server for anybody yet.
    const current = get().agreement;
    if (current?.acceptedAt !== agreement.acceptedAt) return;
    if (current.recordedFor.includes(playerId)) return;
    const next = { ...current, recordedFor: [...current.recordedFor, playerId] };
    writeAgreement(next);
    set({ agreement: next });
  },
}));

export function useAgreementStatus(): AgreementStatus {
  return useAgreementStore((state) => statusOf(state.agreement, state.versions));
}

/**
 * Pull both documents from the server and keep the newer copies, so their
 * versions become the ones a player agrees to. Never throws: offline, or on a
 * refused or malformed reply, the copies the device already has stand.
 */
export async function refreshPolicyVersions(signal?: AbortSignal): Promise<void> {
  await Promise.all(
    (['terms', 'privacy'] as const).map(async (policy) => {
      const bundle = bundledDocument(policy);
      try {
        saveDocument(
          bundle,
          await fetchDocument(bundle.slug, signal ?? new AbortController().signal),
        );
      } catch {
        // The saved or bundled copy stands. The next launch or reconnect retries.
      }
    }),
  );
  useAgreementStore.getState().syncVersions();
}

/** The record to send, or null while the device has agreed to nothing. */
export function deviceAgreement(): Agreement | null {
  return useAgreementStore.getState().agreement;
}
